import { BlockProps } from '@/components/Block/Block';
import EmojiText from '@/components/EmojiText/EmojiText';
import P from '@/components/Redactie/P/P';
import { useBlockLocalUserData } from '@/hooks/useBlockLocalUserData';
import { useBlockRemoteUserData } from '@/hooks/useBlockRemoteUserData';
import SendIcon from '@/resources/CustomIcons/SendIcon';
import { Popover, Transition } from '@headlessui/react';
import {
    mdiCheckCircle,
    mdiCheckDecagram,
    mdiCircleOutline,
    mdiCloseCircle,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import ListEditor from '@/components/Redactie/ListEditor/ListEditor';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';
import { UserMessageBlockViewerContainer } from '@/components/Block/BlockViewerContainer';

import styles from './MissingWordBlock.module.sass';

interface MissingWordBlockData {
    text_before: string;
    text_after: string;
    answers: string[];
    correct_answer_index: number;
}

interface MissingWordBlockLocalData {
    pickedAnswer: number | null;
}

const MissingWordBlock = ({
    subjectId,
    id,
    data,
    uuid,
    updatedAt,
    isEditable,
    isEditorPreview,
    isAnimated,
    onEdited,
    onFinished,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<MissingWordBlockData>({
        text_before: data.text_before ?? 'Zin begin',
        text_after: data.text_after ?? 'en zin eind.',
        answers: data.answers ?? ['antwoord 1', 'antwoord 2'],
        correct_answer_index: data.correct_answer_index ?? 0,
    });

    useEffect(() => {
        if (!isEditable) return;
        onEdited?.({
            uuid,
            data: JSON.parse(
                JSON.stringify({
                    ...blockData,
                    answers: blockData.answers.filter(
                        (answer) => !!answer && answer.length > 0,
                    ),
                }),
            ),
            attachments: {},
        });
    }, [blockData]);

    return (
        <div
            className={`${styles['missingwordblock']} ${
                isEditable ? styles['missingwordblock--editable'] : ''
            }`}
        >
            {isEditable && (
                <MissingWordBlockEditor
                    blockData={blockData}
                    setBlockData={setBlockData}
                />
            )}
            {!isEditable && (
                <MissingWordBlockViewer
                    subjectId={subjectId}
                    id={id}
                    updatedAt={updatedAt}
                    blockData={blockData}
                    isEditorPreview={isEditorPreview}
                    isAnimated={isAnimated}
                    onFinished={onFinished}
                />
            )}
        </div>
    );
};

interface MissingWordBlockEditorProps {
    blockData: MissingWordBlockData;
    setBlockData: (blockData: MissingWordBlockData) => void;
}

const MissingWordBlockEditor = ({
    blockData,
    setBlockData,
}: MissingWordBlockEditorProps) => {
    return (
        <BlockEditorContainer className="bg-violet-300">
            <P
                isEditable
                value={blockData.text_before}
                placeholder="Begin van de zin"
                onChange={(value) => {
                    setBlockData({ ...blockData, text_before: value });
                }}
                maxLength={128}
                rows={1}
            />
            <P
                isEditable
                value={blockData.text_after}
                placeholder="Eind van de zin"
                onChange={(value) => {
                    setBlockData({ ...blockData, text_after: value });
                }}
                maxLength={128}
                rows={1}
            />
            <ListEditor<MissingWordBlockEditorAnswer>
                list={blockData.answers.map((answer, index) => ({
                    text: answer,
                    is_correct: index === blockData.correct_answer_index,
                }))}
                onSaved={(list) => {
                    const answers = list.map((answer) => answer.text);
                    const correct_answer_index = list.findIndex(
                        (answer) => answer.is_correct,
                    );
                    setBlockData({
                        ...blockData,
                        answers,
                        correct_answer_index,
                    });
                }}
                createNewItem={() => ({
                    text: '',
                    is_correct: false,
                })}
                validateItem={(item) => ({
                    isValid: item.text.length > 0,
                    validationText:
                        'Voeg een tekst toe of verwijder dit antwoord',
                })}
                validateList={(list) => ({
                    isValid:
                        list.filter((item) => item.is_correct).length === 1,
                    validationText: 'Er moet exact 1 correct antwoord zijn',
                })}
                minItems={2}
                maxItems={5}
                listDescription={`${blockData.text_before} ... ${blockData.text_after}`}
                listDisplayName="Ontbrekende woorden"
                listItemDisplayName="Ontbrekend woord"
                listSummaryItemData={(item) => ({
                    displayName: item.text,
                    icon: item.is_correct ? mdiCheckCircle : mdiCircleOutline,
                })}
                listItemTemplate={MissingWordBlockAnswerEditor}
            />
        </BlockEditorContainer>
    );
};

interface MissingWordBlockEditorAnswer {
    text: string;
    is_correct: boolean;
}

const MissingWordBlockAnswerEditor = (
    index: number,
    item: MissingWordBlockEditorAnswer,
    setItem: (item: MissingWordBlockEditorAnswer) => void,
) => (
    <div className="rounded-md bg-white outline outline-2 outline-slate-200">
        <P
            isEditable
            maxLength={96}
            onChange={(value) => {
                setItem({
                    ...item,
                    text: value,
                    is_correct: value?.length > 0 ? item.is_correct : false,
                });
            }}
            placeholder={`Antwoord ${index + 1}`}
            value={item.text}
            rows={1}
        >
            {item.text}
        </P>
        <div className="mx-2">
            <FormControlLabel
                value="end"
                onChange={(event) =>
                    setItem({
                        ...item,
                        is_correct:
                            item?.text?.length === 0
                                ? false
                                : (event.target as HTMLInputElement).checked,
                    })
                }
                control={
                    <Checkbox
                        size="small"
                        disabled={item?.text?.length === 0}
                        checked={item.is_correct}
                    />
                }
                label={<span className="text-sm font-bold">Correct</span>}
                labelPlacement="end"
            />
        </div>
    </div>
);

interface MissingWordBlockViewerProps {
    subjectId: number;
    id?: number;
    updatedAt: string;
    blockData: MissingWordBlockData;
    isEditorPreview?: boolean;
    isAnimated?: boolean;
    onFinished?: () => void;
}

const MissingWordBlockViewer = ({
    subjectId,
    id,
    updatedAt,
    blockData,
    isEditorPreview,
    isAnimated,
    onFinished,
}: MissingWordBlockViewerProps) => {
    const { submitUserDataRemote } = useBlockRemoteUserData(id);
    const { userDataLocal, setUserDataLocal } =
        useBlockLocalUserData<MissingWordBlockLocalData>(
            subjectId,
            id,
            updatedAt,
            isEditorPreview,
            {
                pickedAnswer: null,
            },
        );
    const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(
        userDataLocal.pickedAnswer !== null,
    );
    useLayoutEffect(() => {
        if (isAnswerSubmitted) onFinished?.();
        else if (userDataLocal.pickedAnswer !== null) {
            setIsAnswerSubmitted(true);
            onFinished?.();
        }
    }, [isAnswerSubmitted]);

    const answers = useMemo(() => {
        return blockData.answers.filter(
            (answer) => !!answer && answer.length > 0,
        );
    }, [blockData.answers]);

    const pickedRightAnswer = useMemo(() => {
        return userDataLocal.pickedAnswer === blockData.correct_answer_index;
    }, [userDataLocal.pickedAnswer, blockData.correct_answer_index]);

    return (
        <UserMessageBlockViewerContainer
            label="Vul het missende woord in"
            icon={SendIcon}
            isCollapsed={isAnswerSubmitted}
            labelClassName="text-violet-800"
            isAnimated={isAnimated}
        >
            <label className={`${styles['missingwordblock__viewer__text']}`}>
                <span>
                    <EmojiText>{blockData.text_before} </EmojiText>
                </span>
                <span className="sr-only">(missend woord)</span>
                {userDataLocal.pickedAnswer === null && (
                    <Popover className="inline-block">
                        <Popover.Button
                            className={`${styles['missingwordblock__viewer__text__select']}`}
                            aria-disabled={userDataLocal.pickedAnswer !== null}
                        >
                            kies een woord
                        </Popover.Button>
                        <Transition
                            as="div"
                            enter="duration-200 ease-out"
                            enterFrom="transform motion-safe:scale-x-50 motion-safe:scale-y-0 opacity-0"
                            enterTo="transform scale-100 opacity-100"
                            leave="duration-75 ease-out"
                            leaveFrom="transform scale-100 opacity-100"
                            leaveTo="transform motion-safe:scale-x-95 motion-safe:scale-y-0 opacity-0"
                            className="origin-top"
                        >
                            <Popover.Panel
                                className={
                                    styles[
                                        'missingwordblock__viewer__text__select__panel'
                                    ]
                                }
                            >
                                <ul
                                    className={
                                        styles[
                                            'missingwordblock__viewer__text__select__panel__list'
                                        ]
                                    }
                                >
                                    {answers.map((answer, index) => (
                                        <li key={index}>
                                            <button
                                                className="p-2"
                                                aria-pressed={false}
                                                onClick={() => {
                                                    setUserDataLocal({
                                                        pickedAnswer: index,
                                                    });
                                                    submitUserDataRemote(
                                                        index,
                                                    ).then(() =>
                                                        setIsAnswerSubmitted(
                                                            true,
                                                        ),
                                                    );
                                                }}
                                            >
                                                <EmojiText>{answer}</EmojiText>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </Popover.Panel>
                        </Transition>
                    </Popover>
                )}
                {userDataLocal.pickedAnswer !== null && (
                    <span
                        className={`${styles['missingwordblock__viewer__text__answer']}`}
                    >
                        {answers[userDataLocal.pickedAnswer]}
                    </span>
                )}
                <span>
                    <EmojiText> {blockData.text_after}</EmojiText>
                </span>
            </label>
            {userDataLocal.pickedAnswer !== null && (
                <div
                    className={`${styles['missingwordblock__viewer__feedback']}`}
                >
                    {pickedRightAnswer && (
                        <div className="text-violet-700">
                            <Icon path={mdiCheckDecagram} size={1} />
                            Goede antwoord!
                        </div>
                    )}
                    {!pickedRightAnswer && (
                        <div className="text-sm text-red-500">
                            <Icon path={mdiCloseCircle} size={0.8} />
                            Het goede antwoord was:{' '}
                            {answers[blockData.correct_answer_index]}
                        </div>
                    )}
                </div>
            )}
        </UserMessageBlockViewerContainer>
    );
};

export default MissingWordBlock;
